import React, { useEffect, useState, useRef, Component } from 'react';
import { Commission, Badge, LoanDetail, ApprovalStatus } from '../types/commission';
import { Calendar, Briefcase, TrendingUp, DollarSign, Target, Sparkles, Trophy, Medal, Crown, Check, Flag, X, PlusCircle, FileText, Send, ChevronRight, ChevronLeft, BarChart3, Gem, BookOpen, Zap } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { ReviewStatusBanner } from './ReviewStatusBanner';
import { ApprovedBanner } from './ApprovedBanner';
import { CompensationPlanModal } from './CompensationPlanModal';
// Animated count-up hook
function useCountUp(target: number, duration: number = 1500, delay: number = 400) {
  const [value, setValue] = useState(0);
  const startTime = useRef<number | null>(null);
  const rafId = useRef<number>(0);
  useEffect(() => {
    const timeout = setTimeout(() => {
      const animate = (timestamp: number) => {
        if (!startTime.current) startTime.current = timestamp;
        const elapsed = timestamp - startTime.current;
        const progress = Math.min(elapsed / duration, 1);
        const eased = 1 - Math.pow(1 - progress, 3); // easeOutCubic
        setValue(eased * target);
        if (progress < 1) {
          rafId.current = requestAnimationFrame(animate);
        }
      };
      rafId.current = requestAnimationFrame(animate);
    }, delay);
    return () => {
      clearTimeout(timeout);
      cancelAnimationFrame(rafId.current);
    };
  }, [target, duration, delay]);
  return value;
}
interface LoanOfficerViewProps {
  commission: Commission;
  onApproveLoan: (userId: string, loanId: string) => void;
  onApproveAllLoans: (userId: string) => void;
  onFlagLoan: (userId: string, loanId: string, comment: string) => void;
  onSubmitLoanResponse: (userId: string, loanId: string, message: string) => void;
  onApproveLoanResolution: (userId: string, loanId: string) => void;
}
const PAR_BPS = 275; // 2.75% = 275 basis points
function calculateFrontBps(loan: LoanDetail): number {
  if (loan.frontBps !== undefined) return loan.frontBps;
  if (loan.amount === 0) return 0;
  return loan.commission / loan.amount * 10000;
}
function getTierColor(units: number): {
  bg: string;
  text: string;
  border: string;
  accent: string;
  gradient: string;
} {
  if (units >= 9) {
    return {
      bg: 'bg-gradient-to-br from-amber-50 via-orange-50 to-red-50',
      text: 'text-amber-900',
      border: 'border-amber-300',
      accent: 'bg-gradient-to-r from-amber-500 via-orange-500 to-red-500',
      gradient: 'from-amber-500 via-orange-500 to-red-500'
    };
  } else if (units >= 4) {
    return {
      bg: 'bg-gradient-to-br from-violet-50 via-purple-50 to-fuchsia-50',
      text: 'text-violet-800',
      border: 'border-violet-300',
      accent: 'bg-gradient-to-r from-violet-500 via-purple-500 to-fuchsia-500',
      gradient: 'from-violet-500 via-purple-500 to-fuchsia-500'
    };
  } else {
    return {
      bg: 'bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50',
      text: 'text-emerald-800',
      border: 'border-emerald-300',
      accent: 'bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500',
      gradient: 'from-emerald-500 via-teal-500 to-cyan-500'
    };
  }
}
function getBadgeColor(badge: Badge): {
  bg: string;
  text: string;
  border: string;
  accent: string;
  icon: typeof Trophy;
} {
  const colors = {
    Diamond: {
      bg: 'bg-gradient-to-br from-sky-50 via-blue-50 to-indigo-50',
      text: 'text-blue-700',
      border: 'border-blue-300',
      accent: 'bg-gradient-to-r from-sky-400 via-blue-500 to-indigo-500',
      icon: Gem
    },
    Platinum: {
      bg: 'bg-purple-50',
      text: 'text-purple-700',
      border: 'border-purple-200',
      accent: 'bg-gradient-to-r from-purple-500 to-pink-500',
      icon: Trophy
    },
    Gold: {
      bg: 'bg-yellow-50',
      text: 'text-yellow-700',
      border: 'border-yellow-200',
      accent: 'bg-gradient-to-r from-yellow-500 to-amber-500',
      icon: Medal
    }
  };
  return colors[badge];
}
const TIER_THRESHOLDS = [{
  tier: 1,
  name: '1-3 Units',
  min: 1,
  max: 3
}, {
  tier: 2,
  name: '4-8 Units',
  min: 4,
  max: 8
}, {
  tier: 3,
  name: '9+ Units',
  min: 9,
  max: Infinity
}];
// Badge progression thresholds based on lifetime funded volume
const BADGE_THRESHOLDS: {
  badge: Badge;
  minVolume: number;
}[] = [{
  badge: 'Gold',
  minVolume: 0
}, {
  badge: 'Platinum',
  minVolume: 3000000
}, {
  badge: 'Diamond',
  minVolume: 15000000
}];
function getBadgeProgressInfo(lifetimeVolume: number) {
  let currentBadgeIndex = 0;
  for (let i = BADGE_THRESHOLDS.length - 1; i >= 0; i--) {
    if (lifetimeVolume >= BADGE_THRESHOLDS[i].minVolume) {
      currentBadgeIndex = i;
      break;
    }
  }
  const current = BADGE_THRESHOLDS[currentBadgeIndex];
  const next = currentBadgeIndex < BADGE_THRESHOLDS.length - 1 ? BADGE_THRESHOLDS[currentBadgeIndex + 1] : null;
  const volumeToNext = next ? next.minVolume - lifetimeVolume : 0;
  const progress = next ? (lifetimeVolume - current.minVolume) / (next.minVolume - current.minVolume) * 100 : 100;
  return {
    currentBadge: current.badge,
    nextBadge: next?.badge || null,
    volumeToNext,
    progress: Math.min(progress, 100),
    lifetimeVolume,
    nextThreshold: next?.minVolume || 0
  };
}
function getCurrentTierInfo(units: number) {
  const current = TIER_THRESHOLDS.find(t => units >= t.min && units <= t.max) || TIER_THRESHOLDS[0];
  const currentIndex = TIER_THRESHOLDS.findIndex(t => t.tier === current.tier);
  const next = currentIndex < TIER_THRESHOLDS.length - 1 ? TIER_THRESHOLDS[currentIndex + 1] : null;
  const afterNext = currentIndex < TIER_THRESHOLDS.length - 2 ? TIER_THRESHOLDS[currentIndex + 2] : null;
  const unitsToNext = next ? next.min - units : 0;
  const progress = next ? (units - current.min) / (next.min - current.min) * 100 : 100;
  return {
    current,
    next,
    afterNext,
    unitsToNext,
    progress
  };
}
// Loan Detail Panel Component
function LoanDetailPanel({
  loan,
  onClose
}: {
  loan: LoanDetail;
  onClose: () => void;
}) {
  const frontBps = calculateFrontBps(loan);
  const frontPercent = frontBps / 100;
  const parPercent = PAR_BPS / 100;
  const diff = frontPercent - parPercent;
  const isAbovePar = diff > 0.005;
  const isAtPar = Math.abs(diff) < 0.005;
  const maxPercent = Math.max(frontPercent, parPercent) * 1.2;
  const frontBarWidth = frontPercent / maxPercent * 100;
  const parBarWidth = parPercent / maxPercent * 100;
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-end" onClick={onClose} data-id="element-598">
      <motion.div initial={{
      x: '100%'
    }} animate={{
      x: 0
    }} exit={{
      x: '100%'
    }} transition={{
      type: 'spring',
      damping: 30,
      stiffness: 300
    }} onClick={e => e.stopPropagation()} className="bg-white h-full w-full max-w-lg shadow-2xl overflow-y-auto flex flex-col" data-id="element-599">
        {/* Header */}
        <div className="p-6 border-b border-slate-200 flex-shrink-0" data-id="element-600">
          <div className="flex items-center justify-between mb-1" data-id="element-601">
            <div className="flex items-center gap-3" data-id="element-602">
              <div className="p-2.5 rounded-xl bg-brand-blue/10" data-id="element-603">
                <BarChart3 size={22} className="text-brand-blue" data-id="element-604" />
              </div>
              <div data-id="element-605">
                <h2 className="text-xl font-bold text-brand-dark" data-id="element-606">
                  {loan.loanId}
                </h2>
                <p className="text-xs text-slate-500 mt-0.5" data-id="element-607">Loan Detail</p>
              </div>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg transition-colors" data-id="element-608">
              <X size={22} className="text-slate-500" data-id="element-609" />
            </button>
          </div>
        </div>

        {/* Loan Summary */}
        <div className="p-6 space-y-6 flex-1" data-id="element-610">
          <div className="grid grid-cols-2 gap-3" data-id="element-611">
            <div className="bg-slate-50 rounded-xl p-4 border border-slate-200" data-id="element-612">
              <p className="text-[10px] text-slate-500 font-semibold uppercase tracking-wider mb-1" data-id="element-613">
                Loan Amount
              </p>
              <p className="text-lg font-bold text-slate-900 tabular-nums" data-id="element-614">
                $
                {loan.amount.toLocaleString('en-US', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              })}
              </p>
            </div>
            <div className="bg-slate-50 rounded-xl p-4 border border-slate-200" data-id="element-615">
              <p className="text-[10px] text-slate-500 font-semibold uppercase tracking-wider mb-1" data-id="element-616">
                Close Date
              </p>
              <p className="text-lg font-bold text-slate-900" data-id="element-617">{loan.date}</p>
            </div>
            <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-200" data-id="element-618">
              <p className="text-[10px] text-slate-500 font-semibold uppercase tracking-wider mb-1" data-id="element-619">
                Commission
              </p>
              <p className="text-lg font-bold text-emerald-700 tabular-nums" data-id="element-620">
                $
                {loan.commission.toLocaleString('en-US', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              })}
              </p>
            </div>
            <div className="bg-slate-50 rounded-xl p-4 border border-slate-200" data-id="element-621">
              <p className="text-[10px] text-slate-500 font-semibold uppercase tracking-wider mb-1" data-id="element-622">
                Status
              </p>
              <p className="text-lg font-bold text-slate-900 capitalize" data-id="element-623">
                {loan.approvalStatus.replace('_', ' ')}
              </p>
            </div>
          </div>

          {/* Lead Source */}
          {loan.leadSource && <div className="bg-sky-50 rounded-xl p-4 border border-sky-200" data-id="element-624">
              <p className="text-[10px] text-slate-500 font-semibold uppercase tracking-wider mb-1" data-id="element-625">
                Lead Source
              </p>
              <p className="text-lg font-bold text-sky-700" data-id="element-626">
                {loan.leadSource}
              </p>
            </div>}

          {/* BPS vs PAR Section */}
          <div className="bg-white rounded-2xl border-2 border-slate-200 overflow-hidden" data-id="element-627">
            <div className="px-6 py-4 border-b border-slate-200 bg-slate-50" data-id="element-628">
              <h3 className="text-sm font-bold text-brand-dark uppercase tracking-wider" data-id="element-629">
                Front BPS vs PAR
              </h3>
              <p className="text-xs text-slate-500 mt-0.5" data-id="element-630">
                Rate comparison for this loan
              </p>
            </div>

            <div className="p-6 space-y-6" data-id="element-631">
              {/* Percentage Numbers */}
              <div className="grid grid-cols-2 gap-4" data-id="element-632">
                <div className="text-center p-4 rounded-xl bg-brand-blue/5 border border-brand-blue/20" data-id="element-633">
                  <p className="text-[10px] text-slate-500 font-semibold uppercase tracking-wider mb-2" data-id="element-634">
                    Your Front BPS
                  </p>
                  <p className="text-3xl font-bold text-brand-blue tabular-nums" data-id="element-635">
                    {frontPercent.toFixed(2)}%
                  </p>
                </div>
                <div className="text-center p-4 rounded-xl bg-slate-50 border border-slate-200" data-id="element-636">
                  <p className="text-[10px] text-slate-500 font-semibold uppercase tracking-wider mb-2" data-id="element-637">
                    PAR
                  </p>
                  <p className="text-3xl font-bold text-slate-700 tabular-nums" data-id="element-638">
                    {parPercent.toFixed(2)}%
                  </p>
                </div>
              </div>

              {/* Visual Bar Comparison */}
              <div className="space-y-4" data-id="element-639">
                {/* Front BPS Bar */}
                <div data-id="element-640">
                  <div className="flex items-center justify-between mb-2" data-id="element-641">
                    <span className="text-xs font-semibold text-slate-600" data-id="element-642">
                      Your Front BPS
                    </span>
                    <span className="text-xs font-bold text-brand-blue tabular-nums" data-id="element-643">
                      {frontPercent.toFixed(2)}%
                    </span>
                  </div>
                  <div className="h-5 bg-slate-100 rounded-full overflow-hidden" data-id="element-644">
                    <motion.div initial={{
                    width: 0
                  }} animate={{
                    width: `${Math.min(frontBarWidth, 100)}%`
                  }} transition={{
                    duration: 1,
                    delay: 0.2,
                    ease: 'easeOut'
                  }} className="h-full bg-gradient-to-r from-brand-blue to-brand-blue-light rounded-full" data-id="element-645" />
                  </div>
                </div>

                {/* PAR Bar */}
                <div data-id="element-646">
                  <div className="flex items-center justify-between mb-2" data-id="element-647">
                    <span className="text-xs font-semibold text-slate-600" data-id="element-648">
                      PAR
                    </span>
                    <span className="text-xs font-bold text-slate-700 tabular-nums" data-id="element-649">
                      {parPercent.toFixed(2)}%
                    </span>
                  </div>
                  <div className="h-5 bg-slate-100 rounded-full overflow-hidden" data-id="element-650">
                    <motion.div initial={{
                    width: 0
                  }} animate={{
                    width: `${Math.min(parBarWidth, 100)}%`
                  }} transition={{
                    duration: 1,
                    delay: 0.4,
                    ease: 'easeOut'
                  }} className="h-full bg-gradient-to-r from-slate-400 to-slate-500 rounded-full" data-id="element-651" />
                  </div>
                </div>
              </div>

              {/* Difference */}
              <div className={`p-4 rounded-xl border-2 ${isAtPar ? 'bg-slate-50 border-slate-200' : isAbovePar ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'}`} data-id="element-652">
                <div className="flex items-center justify-between" data-id="element-653">
                  <span className="text-sm font-semibold text-slate-700" data-id="element-654">
                    Difference from PAR
                  </span>
                  <span className={`text-lg font-bold tabular-nums ${isAtPar ? 'text-slate-700' : isAbovePar ? 'text-emerald-700' : 'text-amber-700'}`} data-id="element-655">
                    {diff > 0 ? '+' : ''}
                    {diff.toFixed(2)}%
                  </span>
                </div>
                <p className={`text-xs mt-1 ${isAtPar ? 'text-slate-500' : isAbovePar ? 'text-emerald-600' : 'text-amber-600'}`} data-id="element-656">
                  {isAtPar ? 'This loan is at PAR' : isAbovePar ? 'This loan is above PAR' : 'This loan is below PAR'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>;
}
// Generate historical commission data for previous months
function generateHistoricalCommission(baseCommission: Commission, monthOffset: number): Commission {
  const now = new Date();
  const targetDate = new Date(now.getFullYear(), now.getMonth() + monthOffset, 1);
  const monthName = targetDate.toLocaleString('en-US', {
    month: 'long'
  });
  const year = targetDate.getFullYear();
  const payPeriod = `${monthName} ${year}`;
  // Vary the data slightly per month using a simple seed
  const seed = Math.abs(monthOffset);
  const unitVariations = [3, 5, 2, 4, 6, 3, 7, 4, 5, 2, 3, 8];
  const units = unitVariations[seed % unitVariations.length] || baseCommission.units;
  // Generate loans for that month
  const loanCount = Math.max(1, Math.min(units, 5));
  const leadSources = ['Zillow', 'Realtor Referral', 'Bank Referral', 'Website', 'Social Media', 'Past Client', 'Builder', 'Open House'];
  const loans: LoanDetail[] = Array.from({
    length: loanCount
  }, (_, i) => {
    const day = Math.min(5 + i * 7, 28);
    const dateStr = `${year}-${String(targetDate.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    const amount = 250000 + (seed + i) % 5 * 50000;
    const commission = Math.round(amount * 0.005);
    return {
      loanId: `LN-${year}-${String(targetDate.getMonth() + 1).padStart(2, '0')}${String(i + 1).padStart(2, '0')}`,
      amount,
      tier: Math.min(Math.ceil(units / 3), 5) as any,
      commission,
      date: dateStr,
      leadSource: leadSources[(seed + i) % leadSources.length],
      approvalStatus: 'approved' as ApprovalStatus
    };
  });
  const total = loans.reduce((sum, l) => sum + l.commission, 0);
  return {
    ...baseCommission,
    payPeriod,
    units,
    total,
    loans,
    review: {
      commissionId: baseCommission.userId,
      status: 'approved',
      flaggedLoans: [],
      comments: [],
      approvedAt: `${year}-${String(targetDate.getMonth() + 1).padStart(2, '0')}-28`,
      approvedBy: 'System'
    }
  };
}
export function LoanOfficerView({
  commission,
  onApproveLoan,
  onApproveAllLoans,
  onFlagLoan,
  onSubmitLoanResponse,
  onApproveLoanResolution
}: LoanOfficerViewProps) {
  const [flaggingLoanId, setFlaggingLoanId] = useState<string | null>(null);
  const [flagComment, setFlagComment] = useState('');
  const [showCommissionRequest, setShowCommissionRequest] = useState(false);
  const [requestDescription, setRequestDescription] = useState('');
  const [requestAmount, setRequestAmount] = useState('');
  const [requestCategory, setRequestCategory] = useState('');
  const [requestSubmitted, setRequestSubmitted] = useState(false);
  const [selectedLoan, setSelectedLoan] = useState<LoanDetail | null>(null);
  const [showAdvanceRequest, setShowAdvanceRequest] = useState(false);
  const [advanceAmount, setAdvanceAmount] = useState('');
  const [advanceReason, setAdvanceReason] = useState('');
  const [advanceSubmitted, setAdvanceSubmitted] = useState(false);
  const [showCompPlan, setShowCompPlan] = useState(false);
  const [monthOffset, setMonthOffset] = useState(0); // 0 = current, -1 = last month, etc.
  const MAX_HISTORY_MONTHS = 6;
  // Get the active commission based on month offset
  const activeCommission = monthOffset === 0 ? commission : generateHistoricalCommission(commission, monthOffset);
  const isViewingCurrentMonth = monthOffset === 0;
  const canGoForward = monthOffset < 0;
  const canGoBack = monthOffset > -MAX_HISTORY_MONTHS;
  const totalLoanVolume = activeCommission.loans.reduce((acc, loan) => acc + loan.amount, 0);
  const tierInfo = getCurrentTierInfo(activeCommission.units);
  const tierColors = getTierColor(activeCommission.units);
  const lifetimeVolume = activeCommission.profile?.lifetimeFundedVolume || 0;
  const badgeProgress = getBadgeProgressInfo(lifetimeVolume);
  const currentBadge: Badge = badgeProgress.currentBadge;
  const badgeColors = getBadgeColor(currentBadge);
  const BadgeIcon = badgeColors.icon;
  const approvalStatus = activeCommission.review?.status || 'pending';
  const isFullyApproved = approvalStatus === 'approved';
  const hasPendingLoans = activeCommission.loans.some(l => l.approvalStatus === 'pending');
  const handleFlagSubmit = () => {
    if (flaggingLoanId && flagComment.trim()) {
      onFlagLoan(commission.userId, flaggingLoanId, flagComment);
      setFlaggingLoanId(null);
      setFlagComment('');
    }
  };
  const handleCommissionRequestSubmit = () => {
    if (requestDescription.trim() && requestAmount.trim()) {
      setRequestSubmitted(true);
      setTimeout(() => {
        setShowCommissionRequest(false);
        setRequestDescription('');
        setRequestAmount('');
        setRequestCategory('');
        setRequestSubmitted(false);
      }, 2000);
    }
  };
  const handleAdvanceRequestSubmit = () => {
    if (advanceAmount.trim() && advanceReason.trim()) {
      setAdvanceSubmitted(true);
      setTimeout(() => {
        setShowAdvanceRequest(false);
        setAdvanceAmount('');
        setAdvanceReason('');
        setAdvanceSubmitted(false);
      }, 2000);
    }
  };
  return <div className="flex h-full overflow-hidden" data-id="element-657">
      {/* Main Content Area */}
      <motion.div className="flex-1 min-w-0 overflow-y-auto h-full" layout data-id="element-658">
        <div className="max-w-full mx-auto space-y-4 p-4" data-id="element-659">
          {/* Top Action Bar */}
          <motion.div initial={{
          opacity: 0,
          y: -10
        }} animate={{
          opacity: 1,
          y: 0
        }} className="flex items-center justify-end gap-3" data-id="element-660">
            <button onClick={() => setShowCompPlan(!showCompPlan)} className={`flex items-center gap-2 px-5 py-2.5 font-semibold rounded-xl transition-colors shadow-sm text-sm border ${showCompPlan ? 'bg-white text-navy-950 border-white/20 hover:bg-slate-100' : 'bg-navy-800/80 hover:bg-navy-700/80 text-slate-300 border-white/10'}`} data-id="element-661">
              <BookOpen size={18} className={showCompPlan ? 'text-navy-950' : 'text-slate-400'} data-id="element-662" />
              {showCompPlan ? 'Hide Comp Plan' : 'Comp Plan'}
            </button>
            <button onClick={() => setShowCommissionRequest(true)} className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-accent-400 to-accent-500 hover:from-accent-500 hover:to-accent-600 text-navy-950 font-semibold rounded-xl transition-all shadow-sm shadow-accent-500/20 text-sm" data-id="element-663">
              <FileText size={18} data-id="element-664" />
              Commission Request
            </button>
            <button onClick={() => setShowAdvanceRequest(true)} className="flex items-center gap-2 px-5 py-2.5 bg-navy-700/80 hover:bg-navy-600/80 text-white font-semibold rounded-xl transition-colors shadow-sm border border-white/10 text-sm" data-id="element-665">
              <DollarSign size={18} data-id="element-666" />
              Advance Request
            </button>
          </motion.div>

          {/* Approved Banner */}
          {isFullyApproved && activeCommission.review?.approvedAt && activeCommission.review?.approvedBy && <ApprovedBanner approvedBy={activeCommission.review.approvedBy} approvedAt={activeCommission.review.approvedAt} total={activeCommission.total} data-id="element-667" />}

          {/* Review Status Banner */}
          {!isFullyApproved && isViewingCurrentMonth && <ReviewStatusBanner status={approvalStatus} comments={activeCommission.review?.comments || []} commission={activeCommission} onSubmitResponse={msg => console.log('Global response:', msg)} onApproveResolution={() => console.log('Global approve')} onSubmitLoanResponse={onSubmitLoanResponse} data-id="element-668" />}

          {/* Current Tier Progress - ELECTRIC NEON */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: isFullyApproved ? 0.3 : 0.1
        }} className="electric-hero relative overflow-hidden rounded-2xl border border-cyan-500/30 p-8 shadow-lg" data-id="element-669">
            {/* Animated background grid lines */}
            <div className="absolute inset-0 opacity-[0.03]" style={{
            backgroundImage: `linear-gradient(rgba(0,200,255,1) 1px, transparent 1px), linear-gradient(90deg, rgba(0,200,255,1) 1px, transparent 1px)`,
            backgroundSize: '40px 40px'
          }} data-id="element-670" />

            {/* Lightning accent lines */}
            <div className="lightning-line absolute top-4 right-12 w-px h-16 bg-gradient-to-b from-transparent via-cyan-400 to-transparent" data-id="element-671" />
            <div className="lightning-line-delayed absolute bottom-6 left-20 w-px h-12 bg-gradient-to-b from-transparent via-cyan-300 to-transparent" data-id="element-672" />
            <div className="lightning-line absolute top-1/2 right-1/4 w-20 h-px bg-gradient-to-r from-transparent via-cyan-400/50 to-transparent" data-id="element-673" />

            {/* Background icon */}
            <div className="absolute top-0 right-0 w-64 h-64 opacity-[0.04]" data-id="element-674">
              <Zap size={256} className="text-cyan-400" data-id="element-675" />
            </div>

            <div className="relative z-10" data-id="element-676">
              {/* Month Navigation */}
              <div className="flex items-center justify-between mb-6" data-id="element-677">
                <div className="flex items-center gap-3" data-id="element-678">
                  <button className={`p-1.5 rounded-lg border transition-colors shadow-sm ${canGoBack ? 'bg-white/5 hover:bg-white/10 border-cyan-500/20 cursor-pointer' : 'bg-white/5 border-white/10 text-slate-600 cursor-not-allowed opacity-40'}`} title="Previous month" disabled={!canGoBack} onClick={() => canGoBack && setMonthOffset(monthOffset - 1)} data-id="element-679">
                    <ChevronLeft size={18} className={canGoBack ? 'text-cyan-300' : 'text-slate-600'} data-id="element-680" />
                  </button>
                  <div data-id="element-681">
                    <h2 className="text-2xl font-bold text-white neon-text" data-id="element-682">
                      {activeCommission.payPeriod.split(' ')[0]}'s Commission
                    </h2>
                    <p className="text-xs text-cyan-300/60 font-medium mt-0.5" data-id="element-683">
                      {activeCommission.payPeriod}
                      {!isViewingCurrentMonth && <span className="ml-2 text-cyan-400/40" data-id="element-684">
                          · Historical
                        </span>}
                    </p>
                  </div>
                  <button className={`p-1.5 rounded-lg border transition-colors shadow-sm ${canGoForward ? 'bg-white/5 hover:bg-white/10 border-cyan-500/20 cursor-pointer' : 'bg-white/5 border-white/10 text-slate-600 cursor-not-allowed opacity-40'}`} disabled={!canGoForward} title={canGoForward ? 'Next month' : 'No future months'} onClick={() => canGoForward && setMonthOffset(monthOffset + 1)} data-id="element-685">
                    <ChevronRight size={18} className={canGoForward ? 'text-cyan-300' : 'text-slate-600'} data-id="element-686" />
                  </button>
                  {!isViewingCurrentMonth && <button onClick={() => setMonthOffset(0)} className="ml-2 px-3 py-1 text-xs font-semibold text-cyan-300 bg-cyan-500/10 hover:bg-cyan-500/20 border border-cyan-500/20 rounded-lg transition-colors" data-id="element-687">
                      Back to Current
                    </button>}
                </div>

                {/* Badge - Large in hero */}
                <motion.div className="flex items-center gap-3" initial={{
                opacity: 0,
                scale: 0.8
              }} animate={{
                opacity: 1,
                scale: 1
              }} transition={{
                delay: 0.4,
                type: 'spring',
                stiffness: 200
              }} data-id="element-688">
                  <div className="text-right" data-id="element-689">
                    <p className="text-[10px] text-cyan-400/50 font-semibold uppercase tracking-wider mb-0.5" data-id="element-690">
                      Current Badge
                    </p>
                    <p className="text-lg font-bold text-white" style={{
                    textShadow: '0 0 10px rgba(0, 200, 255, 0.4)'
                  }} data-id="element-691">
                      {currentBadge}
                    </p>
                    {badgeProgress.nextBadge && <p className="text-[10px] text-cyan-400/40 mt-0.5 tabular-nums" data-id="element-692">
                        {badgeProgress.progress.toFixed(0)}% →{' '}
                        {badgeProgress.nextBadge}
                      </p>}
                  </div>
                  <motion.div className="relative" animate={currentBadge === 'Diamond' ? {
                  scale: [1, 1.1, 1],
                  rotate: [0, 5, -5, 0]
                } : {}} transition={{
                  duration: 3,
                  repeat: Infinity,
                  repeatDelay: 1.5,
                  ease: 'easeInOut'
                }} data-id="element-693">
                    {/* Glow ring behind badge */}
                    <motion.div className="absolute inset-[-6px] rounded-2xl opacity-40" animate={{
                    boxShadow: ['0 0 15px rgba(0, 200, 255, 0.3), inset 0 0 15px rgba(0, 200, 255, 0.1)', '0 0 30px rgba(0, 200, 255, 0.6), inset 0 0 20px rgba(0, 200, 255, 0.2)', '0 0 15px rgba(0, 200, 255, 0.3), inset 0 0 15px rgba(0, 200, 255, 0.1)']
                  }} transition={{
                    duration: 2,
                    repeat: Infinity,
                    ease: 'easeInOut'
                  }} data-id="element-694" />
                    <div className={`p-3 rounded-2xl ${badgeColors.accent} shadow-lg border border-white/10`} style={{
                    boxShadow: '0 0 20px rgba(0, 200, 255, 0.3)'
                  }} data-id="element-695">
                      {currentBadge === 'Diamond' ? <img src="https://cdn.magicpatterns.com/uploads/8RvJy5EGAXXgvMNsocZX9C/c17a84e6e145fdd1859943ed187b6506.gif" alt="Diamond" className="w-10 h-10 drop-shadow-[0_0_12px_rgba(59,130,246,0.9)]" data-id="element-696" /> : currentBadge === 'Platinum' ? <Trophy size={28} className="text-white drop-shadow-[0_0_8px_rgba(168,85,247,0.8)]" data-id="element-697" /> : <img src="https://cdn.magicpatterns.com/uploads/cFBpWQaxS8FA8wssK4eHmU/Gold.gif" alt="Gold" className="w-10 h-10 drop-shadow-[0_0_8px_rgba(234,179,8,0.8)]" data-id="element-698" />}
                    </div>
                  </motion.div>
                </motion.div>
              </div>

              <div className="flex items-start justify-between mb-6" data-id="element-699">
                <div className="flex items-center gap-4" data-id="element-700">
                  {/* Icon with glow ring */}
                  <div className="glow-ring-wrapper" data-id="element-701">
                    <div className="p-4 rounded-2xl bg-gradient-to-br from-cyan-500 to-blue-600 shadow-lg shadow-cyan-500/30" data-id="element-702">
                      <TrendingUp size={32} className="text-white drop-shadow-[0_0_8px_rgba(0,200,255,0.8)]" data-id="element-703" />
                    </div>
                  </div>
                  <div data-id="element-704">
                    <p className="text-sm font-semibold text-cyan-300/70 uppercase tracking-wider mb-1" data-id="element-705">
                      Current Tier
                    </p>
                    <h1 className="text-5xl font-bold text-white" style={{
                    textShadow: '0 0 20px rgba(0, 200, 255, 0.4), 0 0 40px rgba(0, 200, 255, 0.15)'
                  }} data-id="element-706">
                      {tierInfo.current.name}
                    </h1>
                    <p className="text-xs text-slate-400 mt-1" data-id="element-707">
                      Based on units this period
                    </p>
                  </div>
                </div>
                {tierInfo.next && <motion.div className="text-right next-tier-pulse" animate={{
                scale: [1, 1.04, 1],
                opacity: [0.75, 1, 0.75]
              }} transition={{
                duration: 2,
                repeat: Infinity,
                ease: 'easeInOut'
              }} data-id="element-708">
                    <p className="text-xs font-semibold text-cyan-400/60 uppercase tracking-wider mb-1" data-id="element-709">
                      Next Tier
                    </p>
                    <p className="text-2xl font-bold text-cyan-100" style={{
                  textShadow: '0 0 10px rgba(0, 200, 255, 0.5), 0 0 20px rgba(0, 200, 255, 0.2)'
                }} data-id="element-710">
                      {tierInfo.next.name}
                    </p>
                    {tierInfo.afterNext && <motion.p className="text-xs text-cyan-400/40 font-medium mt-1" animate={{
                  opacity: [0.3, 0.6, 0.3]
                }} transition={{
                  duration: 3,
                  repeat: Infinity,
                  ease: 'easeInOut',
                  delay: 0.5
                }} data-id="element-711">
                        then {tierInfo.afterNext.name}
                      </motion.p>}
                  </motion.div>}
              </div>
              {tierInfo.next ? <ElectricProgressSection tierInfo={tierInfo} isFullyApproved={isFullyApproved} data-id="element-712" /> : <div className="flex items-center gap-2 text-cyan-300 bg-cyan-500/10 px-4 py-3 rounded-xl border border-cyan-500/20" data-id="element-713">
                  <Zap size={18} className="drop-shadow-[0_0_6px_rgba(0,200,255,0.8)]" data-id="element-714" />
                  <span className="text-sm font-bold" data-id="element-715">
                    Congratulations! You've reached the highest tier!
                  </span>
                </div>}
            </div>
          </motion.div>

          {/* Stats Grid */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: isFullyApproved ? 0.4 : 0.2
        }} className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-716">
            <div className="bg-navy-800/70 backdrop-blur-sm rounded-xl border border-white/5 p-4 shadow-sm" data-id="element-717">
              <div className="flex items-center gap-2 mb-2" data-id="element-718">
                <div className="p-2 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-500 shadow-sm shadow-emerald-500/20" data-id="element-719">
                  <DollarSign size={18} className="text-white" data-id="element-720" />
                </div>
                <p className="text-[10px] text-slate-400 font-semibold uppercase tracking-wider" data-id="element-721">
                  Total Commission
                </p>
              </div>
              <p className="text-xl font-bold text-white tracking-tight" data-id="element-722">
                $
                {activeCommission.total.toLocaleString('en-US', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              })}
              </p>
            </div>
            <div className="bg-navy-800/70 backdrop-blur-sm rounded-xl border border-white/5 p-4 shadow-sm" data-id="element-723">
              <div className="flex items-center gap-2 mb-2" data-id="element-724">
                <div className="p-2 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-500 shadow-sm shadow-blue-500/20" data-id="element-725">
                  <Calendar size={18} className="text-white" data-id="element-726" />
                </div>
                <p className="text-[10px] text-slate-400 font-semibold uppercase tracking-wider" data-id="element-727">
                  Total Loan Volume
                </p>
              </div>
              <p className="text-xl font-bold text-white tracking-tight" data-id="element-728">
                $
                {totalLoanVolume.toLocaleString('en-US', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              })}
              </p>
            </div>
            <div className="bg-navy-800/70 backdrop-blur-sm rounded-xl border border-white/5 p-4 shadow-sm" data-id="element-729">
              <div className="flex items-center gap-2 mb-2" data-id="element-730">
                <div className="p-2 rounded-lg bg-gradient-to-br from-purple-500 to-violet-500 shadow-sm shadow-purple-500/20" data-id="element-731">
                  <Briefcase size={18} className="text-white" data-id="element-732" />
                </div>
                <p className="text-[10px] text-slate-400 font-semibold uppercase tracking-wider" data-id="element-733">
                  Total Units
                </p>
              </div>
              <p className="text-xl font-bold text-white tracking-tight" data-id="element-734">
                {activeCommission.units}
              </p>
            </div>
          </motion.div>

          {/* Loans Table */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: isFullyApproved ? 0.5 : 0.3
        }} className="bg-navy-800/70 backdrop-blur-sm rounded-2xl border border-white/5 shadow-lg overflow-hidden" data-id="element-735">
            <div className="relative border-b border-white/5 px-8 py-6" data-id="element-736">
              <div className="relative flex items-center justify-between" data-id="element-737">
                <div data-id="element-738">
                  <h2 className="text-xl font-bold text-white mb-1" data-id="element-739">
                    Your Loans - {activeCommission.payPeriod}
                  </h2>
                  <p className="text-sm text-slate-400 font-medium" data-id="element-740">
                    {activeCommission.loans.length} loan
                    {activeCommission.loans.length !== 1 ? 's' : ''} closed this
                    period
                    <span className="text-slate-500 ml-2" data-id="element-741">
                      · Click a loan for details
                    </span>
                  </p>
                </div>
                <div className="flex items-center gap-4" data-id="element-742">
                  <div className={`relative px-4 py-2 rounded-xl border-2 ${tierColors.border} ${tierColors.bg} shadow-sm`} data-id="element-743">
                    <span className={`text-sm font-bold ${tierColors.text}`} data-id="element-744">
                      {tierInfo.current.name}
                    </span>
                  </div>
                  {hasPendingLoans && isViewingCurrentMonth && <button onClick={() => onApproveAllLoans(activeCommission.userId)} className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-xl transition-colors shadow-sm text-sm" data-id="element-745">
                      <Check size={16} data-id="element-746" />
                      Approve All Pending
                    </button>}
                </div>
              </div>
            </div>

            {activeCommission.loans.length > 0 ? <div className="overflow-x-auto" data-id="element-747">
                <table className="w-full" data-id="element-748">
                  <thead data-id="element-749">
                    <tr className="border-b border-white/5" data-id="element-750">
                      <th className="px-8 py-4 text-left text-[10px] font-bold text-slate-500 uppercase tracking-widest" data-id="element-751">
                        Loan ID
                      </th>
                      <th className="px-8 py-4 text-left text-[10px] font-bold text-slate-500 uppercase tracking-widest" data-id="element-752">
                        Date
                      </th>
                      <th className="px-8 py-4 text-left text-[10px] font-bold text-slate-500 uppercase tracking-widest" data-id="element-753">
                        Lead Source
                      </th>
                      <th className="px-8 py-4 text-right text-[10px] font-bold text-slate-500 uppercase tracking-widest" data-id="element-754">
                        Loan Amount
                      </th>
                      <th className="px-8 py-4 text-right text-[10px] font-bold text-slate-500 uppercase tracking-widest" data-id="element-755">
                        Commission
                      </th>
                      <th className="px-8 py-4 text-right text-[10px] font-bold text-slate-500 uppercase tracking-widest" data-id="element-756">
                        Front BPS
                      </th>
                      <th className="px-8 py-4 text-center text-[10px] font-bold text-slate-500 uppercase tracking-widest" data-id="element-757">
                        Status
                      </th>
                      <th className="px-8 py-4 text-center text-[10px] font-bold text-slate-500 uppercase tracking-widest" data-id="element-758">
                        Actions
                      </th>
                      <th className="px-4 py-4" data-id="element-759"></th>
                    </tr>
                  </thead>
                  <tbody data-id="element-760">
                    {activeCommission.loans.map((loan, index) => {
                  const bps = calculateFrontBps(loan);
                  const bpsDiff = bps - PAR_BPS;
                  return <motion.tr key={loan.loanId} initial={{
                    opacity: 0,
                    x: -20
                  }} animate={{
                    opacity: 1,
                    x: 0
                  }} transition={{
                    delay: 0.05 * index
                  }} className="border-b border-slate-100 last:border-0 hover:bg-white/[0.03] transition-colors group cursor-pointer" onClick={() => setSelectedLoan(loan)} data-id="element-761">
                          <td className="px-8 py-6 whitespace-nowrap" data-id="element-762">
                            <div className="flex items-center gap-3" data-id="element-763">
                              <div className="w-1.5 h-1.5 rounded-full bg-slate-600 group-hover:bg-accent-400 transition-colors" data-id="element-764"></div>
                              <span className="text-sm font-semibold text-slate-200 group-hover:text-accent-400 transition-colors" data-id="element-765">
                                {loan.loanId}
                              </span>
                            </div>
                          </td>
                          <td className="px-8 py-6 whitespace-nowrap" data-id="element-766">
                            <span className="text-sm text-slate-400 font-medium" data-id="element-767">
                              {loan.date}
                            </span>
                          </td>
                          <td className="px-8 py-6 whitespace-nowrap" data-id="element-768">
                            <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-indigo-500/10 text-indigo-300 text-xs font-semibold border border-indigo-500/20" data-id="element-769">
                              {loan.leadSource || '—'}
                            </span>
                          </td>
                          <td className="px-8 py-6 whitespace-nowrap text-right" data-id="element-770">
                            <span className="text-sm font-semibold text-slate-200 tabular-nums" data-id="element-771">
                              $
                              {loan.amount.toLocaleString('en-US', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2
                        })}
                            </span>
                          </td>
                          <td className="px-8 py-6 whitespace-nowrap text-right" data-id="element-772">
                            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-500/10 text-sm font-bold text-emerald-400 tabular-nums border border-emerald-500/20" data-id="element-773">
                              $
                              {loan.commission.toLocaleString('en-US', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2
                        })}
                            </span>
                          </td>
                          <td className="px-8 py-6 whitespace-nowrap text-right" data-id="element-774">
                            <div className="flex items-center justify-end gap-2" data-id="element-775">
                              <span className="text-sm font-bold text-slate-200 tabular-nums" data-id="element-776">
                                {(bps / 100).toFixed(2)}%
                              </span>
                              <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${Math.abs(bpsDiff) < 0.5 ? 'bg-slate-700/50 text-slate-400' : bpsDiff > 0 ? 'bg-emerald-500/10 text-emerald-400' : 'bg-amber-500/10 text-amber-400'}`} data-id="element-777">
                                {bpsDiff > 0 ? '+' : ''}
                                {(bpsDiff / 100).toFixed(2)}%
                              </span>
                            </div>
                          </td>
                          <td className="px-8 py-6 whitespace-nowrap text-center" data-id="element-778">
                            {loan.approvalStatus === 'approved' && <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-emerald-500/10 text-emerald-400 text-xs font-semibold border border-emerald-500/20" data-id="element-779">
                                Approved
                              </span>}
                            {loan.approvalStatus === 'flagged' && <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-amber-500/10 text-amber-400 text-xs font-semibold border border-amber-500/20" data-id="element-780">
                                Flagged
                              </span>}
                            {loan.approvalStatus === 'under_review' && <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-blue-500/10 text-blue-400 text-xs font-semibold border border-blue-500/20" data-id="element-781">
                                Under Review
                              </span>}
                            {loan.approvalStatus === 'resolved_pending_approval' && <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-emerald-500/10 text-emerald-400 text-xs font-semibold border border-emerald-500/20" data-id="element-782">
                                Resolved
                              </span>}
                            {loan.approvalStatus === 'pending' && <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-slate-500/10 text-slate-400 text-xs font-semibold border border-slate-500/20" data-id="element-783">
                                Pending
                              </span>}
                          </td>
                          <td className="px-8 py-6 whitespace-nowrap text-center" data-id="element-784">
                            <div className="flex items-center justify-center gap-2" onClick={e => e.stopPropagation()} data-id="element-785">
                              {loan.approvalStatus === 'pending' && isViewingCurrentMonth && <>
                                    <button onClick={() => onApproveLoan(activeCommission.userId, loan.loanId)} className="p-1.5 rounded-lg bg-emerald-100 text-emerald-700 hover:bg-emerald-200 transition-colors" title="Approve Loan" data-id="element-786">
                                      <Check size={16} data-id="element-787" />
                                    </button>
                                    <button onClick={() => setFlaggingLoanId(loan.loanId)} className="p-1.5 rounded-lg bg-amber-100 text-amber-700 hover:bg-amber-200 transition-colors" title="Flag Loan" data-id="element-788">
                                      <Flag size={16} data-id="element-789" />
                                    </button>
                                  </>}
                              {loan.approvalStatus === 'pending' && !isViewingCurrentMonth && <Check size={20} className="text-emerald-500" data-id="element-790" />}
                              {loan.approvalStatus === 'resolved_pending_approval' && isViewingCurrentMonth && <button onClick={() => onApproveLoanResolution(activeCommission.userId, loan.loanId)} className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 transition-colors text-xs font-semibold" data-id="element-791">
                                    <Check size={14} data-id="element-792" />
                                    Accept
                                  </button>}
                              {loan.approvalStatus === 'approved' && <Check size={20} className="text-emerald-500" data-id="element-793" />}
                              {(loan.approvalStatus === 'flagged' || loan.approvalStatus === 'under_review') && isViewingCurrentMonth && <div className="text-xs text-slate-400 italic" data-id="element-794">
                                    In review
                                  </div>}
                            </div>
                          </td>
                          <td className="px-4 py-6 whitespace-nowrap" data-id="element-795">
                            <ChevronRight size={18} className="text-slate-600 group-hover:text-accent-400 transition-colors" data-id="element-796" />
                          </td>
                        </motion.tr>;
                })}
                  </tbody>
                  <tfoot data-id="element-797">
                    <tr className="bg-white/[0.02] border-t border-white/10" data-id="element-798">
                      <td colSpan={4} className="px-8 py-6" data-id="element-799">
                        <span className="text-sm font-bold text-slate-300 uppercase tracking-wide" data-id="element-800">
                          Total
                        </span>
                      </td>
                      <td className="px-8 py-6 text-right" data-id="element-801">
                        <span className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-emerald-500/10 text-base font-bold text-emerald-400 tabular-nums shadow-sm border border-emerald-500/20" data-id="element-802">
                          $
                          {activeCommission.total.toLocaleString('en-US', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                      })}
                        </span>
                      </td>
                      <td colSpan={4} data-id="element-803"></td>
                    </tr>
                  </tfoot>
                </table>
              </div> : <div className="px-8 py-20 text-center" data-id="element-804">
                <div className="text-slate-600 mb-4" data-id="element-805">
                  <Briefcase size={64} className="mx-auto" data-id="element-806" />
                </div>
                <p className="text-slate-300 font-semibold text-lg mb-2" data-id="element-807">
                  No loans for this period
                </p>
                <p className="text-sm text-slate-500" data-id="element-808">
                  Your closed loans will appear here
                </p>
              </div>}
          </motion.div>
        </div>
      </motion.div>

      {/* Side Panel */}
      <AnimatePresence mode="wait" data-id="element-809">
        {showCompPlan && <CompensationPlanModal onClose={() => setShowCompPlan(false)} data-id="element-810" />}
      </AnimatePresence>

      {/* Loan Detail Panel */}
      <AnimatePresence data-id="element-811">
        {selectedLoan && <LoanDetailPanel loan={selectedLoan} onClose={() => setSelectedLoan(null)} data-id="element-812" />}
      </AnimatePresence>

      {/* Flag Loan Modal */}
      <AnimatePresence data-id="element-813">
        {flaggingLoanId && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setFlaggingLoanId(null)} data-id="element-814">
            <motion.div initial={{
          scale: 0.95,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} exit={{
          scale: 0.95,
          opacity: 0
        }} onClick={e => e.stopPropagation()} className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden" data-id="element-815">
              <div className="p-6 border-b border-slate-200 flex justify-between items-center" data-id="element-816">
                <h3 className="text-lg font-bold text-slate-900" data-id="element-817">
                  Flag Loan {flaggingLoanId}
                </h3>
                <button onClick={() => setFlaggingLoanId(null)} className="text-slate-400 hover:text-slate-600" data-id="element-818">
                  <X size={20} data-id="element-819" />
                </button>
              </div>
              <div className="p-6" data-id="element-820">
                <label className="block text-sm font-semibold text-slate-700 mb-2" data-id="element-821">
                  Reason for flagging <span className="text-red-500" data-id="element-822">*</span>
                </label>
                <textarea value={flagComment} onChange={e => setFlagComment(e.target.value)} placeholder="Describe the issue..." className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none h-32" autoFocus data-id="element-823" />
              </div>
              <div className="p-6 border-t border-slate-200 flex gap-3" data-id="element-824">
                <button onClick={() => setFlaggingLoanId(null)} className="flex-1 px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold rounded-xl transition-colors" data-id="element-825">
                  Cancel
                </button>
                <button onClick={handleFlagSubmit} disabled={!flagComment.trim()} className="flex-1 px-4 py-2 bg-amber-600 hover:bg-amber-700 disabled:bg-slate-300 disabled:cursor-not-allowed text-white font-semibold rounded-xl transition-colors" data-id="element-826">
                  Submit Flag
                </button>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>

      {/* Commission Request Modal */}
      <AnimatePresence data-id="element-827">
        {showCommissionRequest && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => {
        if (!requestSubmitted) setShowCommissionRequest(false);
      }} data-id="element-828">
            <motion.div initial={{
          scale: 0.95,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} exit={{
          scale: 0.95,
          opacity: 0
        }} onClick={e => e.stopPropagation()} className="bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden" data-id="element-829">
              {requestSubmitted ? <motion.div initial={{
            opacity: 0,
            scale: 0.9
          }} animate={{
            opacity: 1,
            scale: 1
          }} className="p-12 text-center" data-id="element-830">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-emerald-100 flex items-center justify-center" data-id="element-831">
                    <Check size={32} className="text-emerald-600" data-id="element-832" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-2" data-id="element-833">
                    Request Submitted
                  </h3>
                  <p className="text-sm text-slate-500" data-id="element-834">
                    Your commission request has been submitted for admin review.
                  </p>
                </motion.div> : <>
                  <div className="p-6 border-b border-slate-200" data-id="element-835">
                    <div className="flex items-center justify-between" data-id="element-836">
                      <div className="flex items-center gap-3" data-id="element-837">
                        <div className="p-2 rounded-lg bg-brand-blue/10" data-id="element-838">
                          <FileText size={20} className="text-brand-blue" data-id="element-839" />
                        </div>
                        <h3 className="text-lg font-bold text-slate-900" data-id="element-840">
                          Commission Request
                        </h3>
                      </div>
                      <button onClick={() => setShowCommissionRequest(false)} className="p-2 hover:bg-slate-100 rounded-lg transition-colors" data-id="element-841">
                        <X size={20} className="text-slate-500" data-id="element-842" />
                      </button>
                    </div>
                    <p className="text-sm text-slate-500 mt-2 ml-11" data-id="element-843">
                      Non loan related commission request
                    </p>
                  </div>
                  <div className="p-6 space-y-5" data-id="element-844">
                    <div data-id="element-845">
                      <label className="block text-sm font-semibold text-slate-700 mb-2" data-id="element-846">
                        Category
                      </label>
                      <select value={requestCategory} onChange={e => setRequestCategory(e.target.value)} className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-blue bg-white text-sm text-slate-900 appearance-none" data-id="element-847">
                        <option value="" data-id="element-848">Select a category...</option>
                        <option value="bonus" data-id="element-849">Bonus</option>
                        <option value="override" data-id="element-850">Override</option>
                        <option value="training" data-id="element-851">Training Incentive</option>
                        <option value="adjustment" data-id="element-852">Tier Adjustment</option>
                        <option value="other" data-id="element-853">Other</option>
                      </select>
                    </div>
                    <div data-id="element-854">
                      <label className="block text-sm font-semibold text-slate-700 mb-2" data-id="element-855">
                        Amount <span className="text-red-500" data-id="element-856">*</span>
                      </label>
                      <div className="relative" data-id="element-857">
                        <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 font-medium" data-id="element-858">
                          $
                        </span>
                        <input type="number" step="0.01" min="0" value={requestAmount} onChange={e => setRequestAmount(e.target.value)} placeholder="0.00" className="w-full pl-8 pr-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-blue text-sm tabular-nums" data-id="element-859" />
                      </div>
                    </div>
                    <div data-id="element-860">
                      <label className="block text-sm font-semibold text-slate-700 mb-2" data-id="element-861">
                        Description <span className="text-red-500" data-id="element-862">*</span>
                      </label>
                      <textarea value={requestDescription} onChange={e => setRequestDescription(e.target.value)} placeholder="Describe the reason for this commission request..." className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-blue resize-none text-sm" rows={4} data-id="element-863" />
                    </div>
                  </div>
                  <div className="p-6 border-t border-slate-200 flex gap-3" data-id="element-864">
                    <button onClick={() => setShowCommissionRequest(false)} className="flex-1 px-4 py-3 bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold rounded-xl transition-colors" data-id="element-865">
                      Cancel
                    </button>
                    <button onClick={handleCommissionRequestSubmit} disabled={!requestDescription.trim() || !requestAmount.trim()} className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-brand-blue hover:bg-brand-blue-light disabled:bg-slate-300 disabled:cursor-not-allowed text-white font-semibold rounded-xl transition-colors" data-id="element-866">
                      <Send size={16} data-id="element-867" />
                      Submit Request
                    </button>
                  </div>
                </>}
            </motion.div>
          </motion.div>}
      </AnimatePresence>

      {/* Advance Request Modal */}
      <AnimatePresence data-id="element-868">
        {showAdvanceRequest && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => {
        if (!advanceSubmitted) setShowAdvanceRequest(false);
      }} data-id="element-869">
            <motion.div initial={{
          scale: 0.95,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} exit={{
          scale: 0.95,
          opacity: 0
        }} onClick={e => e.stopPropagation()} className="bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden" data-id="element-870">
              {advanceSubmitted ? <motion.div initial={{
            opacity: 0,
            scale: 0.9
          }} animate={{
            opacity: 1,
            scale: 1
          }} className="p-12 text-center" data-id="element-871">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-emerald-100 flex items-center justify-center" data-id="element-872">
                    <Check size={32} className="text-emerald-600" data-id="element-873" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-2" data-id="element-874">
                    Advance Request Submitted
                  </h3>
                  <p className="text-sm text-slate-500" data-id="element-875">
                    Your advance request has been submitted for admin review.
                  </p>
                </motion.div> : <>
                  <div className="p-6 border-b border-slate-200" data-id="element-876">
                    <div className="flex items-center justify-between" data-id="element-877">
                      <div className="flex items-center gap-3" data-id="element-878">
                        <div className="p-2 rounded-lg bg-brand-dark/10" data-id="element-879">
                          <DollarSign size={20} className="text-brand-dark" data-id="element-880" />
                        </div>
                        <h3 className="text-lg font-bold text-slate-900" data-id="element-881">
                          Advance Request
                        </h3>
                      </div>
                      <button onClick={() => setShowAdvanceRequest(false)} className="p-2 hover:bg-slate-100 rounded-lg transition-colors" data-id="element-882">
                        <X size={20} className="text-slate-500" data-id="element-883" />
                      </button>
                    </div>
                    <p className="text-sm text-slate-500 mt-2 ml-11" data-id="element-884">
                      Request an advance on your expected commission payout
                    </p>
                  </div>

                  <div className="p-6 space-y-5" data-id="element-885">
                    <div data-id="element-886">
                      <label className="block text-sm font-semibold text-slate-700 mb-2" data-id="element-887">
                        Advance Amount <span className="text-red-500" data-id="element-888">*</span>
                      </label>
                      <div className="relative" data-id="element-889">
                        <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 font-medium" data-id="element-890">
                          $
                        </span>
                        <input type="number" step="0.01" min="0" value={advanceAmount} onChange={e => setAdvanceAmount(e.target.value)} placeholder="0.00" className="w-full pl-8 pr-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-blue text-sm tabular-nums" autoFocus data-id="element-891" />
                      </div>
                      <p className="text-xs text-slate-400 mt-1.5" data-id="element-892">
                        Current commission total: $
                        {activeCommission.total.toLocaleString('en-US', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  })}
                      </p>
                    </div>

                    <div data-id="element-893">
                      <label className="block text-sm font-semibold text-slate-700 mb-2" data-id="element-894">
                        Reason <span className="text-red-500" data-id="element-895">*</span>
                      </label>
                      <textarea value={advanceReason} onChange={e => setAdvanceReason(e.target.value)} placeholder="Explain why you need an advance on your commission..." className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-blue resize-none text-sm" rows={4} data-id="element-896" />
                    </div>
                  </div>

                  <div className="p-6 border-t border-slate-200 flex gap-3" data-id="element-897">
                    <button onClick={() => setShowAdvanceRequest(false)} className="flex-1 px-4 py-3 bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold rounded-xl transition-colors" data-id="element-898">
                      Cancel
                    </button>
                    <button onClick={handleAdvanceRequestSubmit} disabled={!advanceAmount.trim() || !advanceReason.trim()} className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-brand-dark hover:bg-brand-dark/90 disabled:bg-slate-300 disabled:cursor-not-allowed text-white font-semibold rounded-xl transition-colors" data-id="element-899">
                      <Send size={16} data-id="element-900" />
                      Submit Request
                    </button>
                  </div>
                </>}
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}
// Electric Progress Section with count-up animation
function ElectricProgressSection({
  tierInfo,
  isFullyApproved
}: {
  tierInfo: ReturnType<typeof getCurrentTierInfo>;
  isFullyApproved: boolean;
}) {
  const animatedProgress = useCountUp(parseFloat(tierInfo.progress.toFixed(1)), 1500, isFullyApproved ? 600 : 300);
  if (!tierInfo.next) return null;
  return <div className="space-y-3" data-id="element-901">
      <div className="flex justify-between items-center" data-id="element-902">
        <div className="flex items-center gap-2" data-id="element-903">
          <Zap size={16} className="text-cyan-400 drop-shadow-[0_0_4px_rgba(0,200,255,0.6)]" data-id="element-904" />
          <span className="text-sm font-semibold text-cyan-200/80" data-id="element-905">
            Progress to {tierInfo.next.name}
          </span>
        </div>
        <span className="text-sm font-bold text-cyan-300 tabular-nums" style={{
        textShadow: '0 0 8px rgba(0, 200, 255, 0.6)'
      }} data-id="element-906">
          {animatedProgress.toFixed(1)}%
        </span>
      </div>
      {/* Progress bar */}
      <div className="relative h-5 bg-slate-800/80 rounded-full overflow-hidden border border-cyan-500/20 shadow-inner" data-id="element-907">
        <motion.div initial={{
        width: 0
      }} animate={{
        width: `${Math.min(tierInfo.progress, 100)}%`
      }} transition={{
        duration: 1.5,
        delay: isFullyApproved ? 0.6 : 0.3,
        ease: 'easeOut'
      }} className="h-full bg-gradient-to-r from-cyan-500 via-cyan-400 to-blue-400 rounded-full relative" style={{
        boxShadow: '0 0 12px rgba(0, 200, 255, 0.5), 0 0 25px rgba(0, 200, 255, 0.2)'
      }} data-id="element-908">
          {/* Subtle shimmer */}
          <div className="electric-arc-shimmer" data-id="element-909" />
          {/* Glowing tip */}
          <div className="absolute right-0 top-0 bottom-0 w-2 bg-white rounded-full shadow-[0_0_8px_rgba(255,255,255,0.8),0_0_20px_rgba(0,200,255,0.8)]" data-id="element-910" />
        </motion.div>
      </div>
      <div className="flex items-center gap-2" data-id="element-911">
        <Zap size={14} className="text-cyan-400 opacity-60" data-id="element-912" />
        <p className="text-sm text-slate-400 font-medium" data-id="element-913">
          <span className="font-bold text-cyan-200" style={{
          textShadow: '0 0 6px rgba(0, 200, 255, 0.3)'
        }} data-id="element-914">
            {tierInfo.unitsToNext}{' '}
            {tierInfo.unitsToNext === 1 ? 'unit' : 'units'}
          </span>{' '}
          needed to unlock {tierInfo.next.name}
        </p>
      </div>
    </div>;
}